import { z } from "zod";
import { NostrSignedEvent } from "./agent-identity.js";
const Id = z.string().min(1).max(200),
  Hash = z.string().regex(/^[0-9a-f]{64}$/);
const AuditDetails = z
  .object({
    approvalId: Id.optional(),
    workflowId: Id.optional(),
    workflowRevision: z.number().int().positive().optional(),
    stepId: Id.optional(),
    approverUserId: Id.optional(),
    reviewHash: Hash.optional(),
    approvalOutcome: z
      .enum(["pending", "approved", "declined", "expired", "cancelled", "invalidated"])
      .optional(),
    decidedAt: z.string().optional(),
    expiresAt: z.string().optional(),
    continuationOnly: z.literal(true).optional(),
    active: z.boolean().optional(),
    enabled: z.boolean().optional(),
    mode: z.enum(["ask", "auto"]).optional(),
    published: z.boolean().optional(),
    passwordProtected: z.boolean().optional(),
    changedFields: z.array(z.string().max(80)).max(120).optional(),
    runId: Id.optional(),
    effectKind: z.string().max(80).optional(),
    decision: z
      .enum(["approve", "approved", "accept", "reject", "rejected", "deny", "ask"])
      .optional(),
    sourceSeq: z.number().int().nonnegative().optional(),
    securityEventId: Id.optional(),
    tool: z.string().max(80).optional(),
    toolRef: Hash.optional(),
    executionRef: Hash.optional(),
    outcome: z.enum(["succeeded", "error", "paused"]).optional(),
    durationMs: z.number().int().nonnegative().optional(),
  })
  .strict();
export const AuditOutcome = z.enum([
  "recorded",
  "attempted",
  "accepted",
  "succeeded",
  "failed",
  "paused",
  "cancelled",
  "unknown",
]);
export const AuditStatement = z
  .object({
    protocol: z.literal("wurkees.audit-statement/v1"),
    id: Id,
    at: z.string(),
    spaceId: Id.nullable(),
    audience: z.enum(["owner", "channel", "space", "operator"]),
    audienceId: z.string().max(200),
    actor: z
      .object({
        kind: z.enum(["person", "agent", "integration", "system"]),
        userId: Id.nullable(),
        botId: Id.nullable(),
        runId: Id.nullable(),
        publicKey: Hash.nullable(),
      })
      .strict(),
    requesterUserId: Id.nullable(),
    requestId: Id.nullable(),
    action: z.string().min(1).max(240),
    target: z.object({ type: z.string().max(120), id: Id.nullable() }).strict(),
    outcome: AuditOutcome,
    details: AuditDetails,
  })
  .strict();
export type AuditStatementValue = z.infer<typeof AuditStatement>;
export const AuditChainView = z.object({
  id: Id,
  spaceId: Id.nullable(),
  audience: z.string(),
  audienceId: z.string(),
  publicKey: Hash,
  npub: z.string(),
  seq: z.number().int().nonnegative(),
  hash: Hash,
  createdAt: z.string(),
  label: z.string(),
  pending: z.number().int().nonnegative(),
});
export const AuditReceipt = z
  .object({
    id: Id,
    chainId: Id,
    seq: z.number().int().positive(),
    prevHash: Hash,
    hash: Hash,
    statement: AuditStatement,
    signature: NostrSignedEvent,
    signedAt: z.string(),
  })
  .strict();
export type AuditReceiptValue = z.infer<typeof AuditReceipt>;
export const AuditCheckpoint = z
  .object({
    protocol: z.literal("wurkees.audit-checkpoint/v1"),
    chainId: Id,
    seq: z.number().int().nonnegative(),
    hash: Hash,
    at: z.string(),
    signature: NostrSignedEvent,
  })
  .strict();
export type AuditCheckpointValue = z.infer<typeof AuditCheckpoint>;
export const AuditBundle = z
  .object({
    format: z.literal("wurkees-signed-audit"),
    version: z.literal(1),
    chain: AuditChainView,
    fromSeq: z.number().int().positive(),
    receipts: z.array(AuditReceipt).max(1000),
    checkpoint: AuditCheckpoint,
  })
  .strict();
export type AuditBundleValue = z.infer<typeof AuditBundle>;
export const AuditVerification = z.object({
  valid: z.boolean(),
  reason: z.string(),
  checked: z.number().int().nonnegative(),
  fromSeq: z.number().int().nonnegative(),
  toSeq: z.number().int().nonnegative(),
  checkpointReached: z.boolean(),
  completeHistory: z.boolean(),
  publicKey: Hash.nullable(),
});
export const AuditTarget = z.object({ chainId: Id }).strict();
export const AuditPageInput = AuditTarget.extend({
  beforeSeq: z.number().int().positive().optional(),
  actorKind: z.enum(["person", "agent", "integration", "system"]).optional(),
  outcome: AuditOutcome.optional(),
  search: z.string().trim().max(120).optional(),
}).strict();
export const AuditPage = z.object({
  chain: AuditChainView,
  receipts: z.array(AuditReceipt),
  nextBefore: z.number().int().positive().nullable(),
});
export const AuditExportInput = AuditTarget.extend({
  fromSeq: z.number().int().positive().optional(),
}).strict();
export const AuditVerifyInput = AuditTarget.extend({
  fromSeq: z.number().int().positive().optional(),
  checkpoint: AuditCheckpoint.optional(),
}).strict();
export const AuditVerifyBundle = z
  .object({
    bundle: AuditBundle,
    expectedPublicKey: z.string().min(1).max(100),
    checkpoint: AuditCheckpoint.optional(),
  })
  .strict();
